import { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Appointment, 
  updateAppointmentStatus, 
  updateAppointmentCalendarEventId,
  STATUS_OPTIONS,
  AppointmentStatus 
} from '@/lib/appointments-service';
import AppointmentStatusBadge from './AppointmentStatusBadge';
import ManualPaymentDialog from './ManualPaymentDialog';
import CancelAppointmentDialog from './CancelAppointmentDialog';
import { 
  Mail, 
  Phone, 
  Calendar, 
  Video, 
  RefreshCw, 
  Send,
  MapPin,
  User,
  CreditCard,
  FileText,
  Clock,
  Globe,
  Loader2,
  ExternalLink,
  Banknote,
  Building,
  Wifi,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface AppointmentDetailDialogProps {
  appointment: Appointment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAppointmentUpdated: () => void;
  canEdit: boolean;
}

const AppointmentDetailDialog = ({
  appointment,
  open,
  onOpenChange,
  onAppointmentUpdated,
  canEdit,
}: AppointmentDetailDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [showManualPaymentDialog, setShowManualPaymentDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  if (!appointment) return null;
  
  const isCancelled = appointment.payment_status === 'cancelled';
  const canCancel = canEdit && !isCancelled;

  const handleStatusChange = async () => {
    if (!newStatus || newStatus === appointment.payment_status) return;
    
    setLoading('status');
    try {
      await updateAppointmentStatus(appointment.id, newStatus as AppointmentStatus);
      toast({
        title: 'Status Updated',
        description: `Appointment status changed to ${newStatus}`,
      });
      onAppointmentUpdated();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const handleResendEmails = async () => {
    setLoading('email');
    try {
      // Send client confirmation email
      const { error: clientError } = await supabase.functions.invoke('send-booking-confirmation', {
        body: {
          bookingId: appointment.booking_id,
          email: appointment.email,
          fullName: appointment.full_name,
          service: appointment.service,
          appointmentType: appointment.appointment_type,
          appointmentStart: appointment.appointment_start,
          paymentMethod: appointment.payment_method,
          language: appointment.language || 'en',
          zoomJoinUrl: appointment.zoom_join_url,
          zoomPassword: appointment.zoom_password,
        },
      });

      if (clientError) throw clientError;

      // Send team notification email
      const { error: teamError } = await supabase.functions.invoke('notify-team-booking', {
        body: {
          bookingId: appointment.booking_id,
          fullName: appointment.full_name,
          email: appointment.email,
          phone: appointment.phone,
          service: appointment.service,
          appointmentType: appointment.appointment_type,
          appointmentStart: appointment.appointment_start,
          situation: appointment.situation,
          paymentMethod: appointment.payment_method,
          language: appointment.language || 'en',
          zoomJoinUrl: appointment.zoom_join_url,
          zoomStartUrl: appointment.zoom_start_url,
        },
      });

      if (teamError) throw teamError;

      toast({
        title: 'Emails Sent',
        description: 'Confirmation emails have been resent successfully',
      });
    } catch (error) {
      console.error('Error resending emails:', error);
      toast({
        title: 'Error',
        description: 'Failed to resend emails',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const handleRecreateCalendarEvent = async () => {
    if (!appointment.appointment_start) {
      toast({
        title: 'Error',
        description: 'No appointment date/time set',
        variant: 'destructive',
      });
      return;
    }

    setLoading('calendar');
    try {
      const startTime = new Date(appointment.appointment_start);
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour duration

      let description = `Client: ${appointment.full_name}\nEmail: ${appointment.email}\nPhone: ${appointment.phone}\nService: ${appointment.service}\n\nSituation:\n${appointment.situation}`;
      
      if (appointment.appointment_type === 'In-person') {
        description += '\n\n📍 In-Person Appointment at:\n10925 Beechnut St A106, Houston, TX 77072';
      } else if (appointment.appointment_type === 'Phone') {
        description += '\n\n📞 Phone Consultation - Client will be called';
      }

      const { data, error } = await supabase.functions.invoke('create-calendar-event', {
        body: {
          summary: `${appointment.appointment_type === 'Video' ? '🎥' : appointment.appointment_type === 'Phone' ? '📞' : '🏢'} Legal Consultation - ${appointment.full_name}`,
          description,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          timezone: 'America/Chicago',
          attendee_email: appointment.email,
          attendee_name: appointment.full_name,
          zoom_join_url: appointment.zoom_join_url,
        },
      });

      if (error) throw error;

      // Update the appointment with the new calendar event ID
      if (data?.event_id) {
        await updateAppointmentCalendarEventId(appointment.id, data.event_id);
      }

      toast({
        title: 'Calendar Event Created',
        description: 'Google Calendar event has been created successfully',
      });
      onAppointmentUpdated();
    } catch (error) {
      console.error('Error creating calendar event:', error);
      toast({
        title: 'Error',
        description: 'Failed to create calendar event',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Not scheduled';
    return format(new Date(dateString), 'PPpp');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Appointment Details
          </DialogTitle>
          <DialogDescription>
            Booking ID: <span className="font-mono">{appointment.booking_id}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Actions */}
          <div className="flex flex-wrap items-center gap-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <AppointmentStatusBadge status={appointment.payment_status || 'pending'} />
            </div>
            
            {canEdit && (
              <div className="flex items-center gap-2 ml-auto">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Change to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  size="sm" 
                  onClick={handleStatusChange}
                  disabled={!newStatus || newStatus === appointment.payment_status || loading === 'status'}
                >
                  {loading === 'status' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                </Button>
              </div>
            )}
          </div>

          {/* Client Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium">{appointment.full_name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Date of Birth</p>
                <p className="font-medium">{format(new Date(appointment.date_of_birth), 'PP')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Email</p>
                <a href={`mailto:${appointment.email}`} className="font-medium text-primary hover:underline flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  {appointment.email}
                </a>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Phone</p>
                <a href={`tel:${appointment.phone}`} className="font-medium text-primary hover:underline flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {appointment.phone}
                </a>
              </div>
              {appointment.address && (
                <div className="space-y-1 md:col-span-2">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {appointment.address}
                  </p>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Preferred Language</p>
                <p className="font-medium flex items-center gap-1">
                  <Globe className="h-4 w-4" />
                  {appointment.language?.toUpperCase() || 'EN'}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Appointment Details */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Appointment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Service</p>
                <p className="font-medium">{appointment.service}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="font-medium flex items-center gap-1">
                  {appointment.appointment_type === 'Video' && <Video className="h-4 w-4" />}
                  {appointment.appointment_type === 'Phone' && <Phone className="h-4 w-4" />}
                  {appointment.appointment_type === 'In-person' && <MapPin className="h-4 w-4" />}
                  {appointment.appointment_type || 'Not specified'}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Date & Time</p>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDateTime(appointment.appointment_start)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Google Calendar</p>
                <p className="font-medium">
                  {appointment.calendar_event_id ? (
                    <Badge variant="outline" className="bg-green-50">
                      ✓ Event Created
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-yellow-50">
                      ⚠ No Event
                    </Badge>
                  )}
                </p>
              </div>
            </div>

            {/* Zoom Information */}
            {appointment.zoom_join_url && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Zoom Meeting</p>
                <div className="space-y-2">
                  <a 
                    href={appointment.zoom_join_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Join URL
                  </a>
                  {appointment.zoom_password && (
                    <p className="text-sm">Password: <span className="font-mono">{appointment.zoom_password}</span></p>
                  )}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Situation/Description */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Situation Description
            </h3>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm whitespace-pre-wrap">{appointment.situation}</p>
            </div>
          </div>

          <Separator />

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Status</p>
                <AppointmentStatusBadge status={appointment.payment_status || 'pending'} />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="font-medium">
                  ${((appointment.payment_amount || 15000) / 100).toFixed(2)} {appointment.payment_currency?.toUpperCase() || 'USD'}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Method</p>
                <p className="font-medium capitalize">{appointment.payment_method || 'Not specified'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Source</p>
                <div className="flex items-center gap-1.5">
                  {appointment.payment_source === 'offline' ? (
                    <>
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Offline / In-Office</span>
                    </>
                  ) : appointment.stripe_session_id ? (
                    <>
                      <Wifi className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Online (Stripe)</span>
                    </>
                  ) : (
                    <span className="font-medium text-muted-foreground">Not recorded</span>
                  )}
                </div>
              </div>
              {appointment.payment_date && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Payment Date</p>
                  <p className="font-medium">{format(new Date(appointment.payment_date), 'PPp')}</p>
                </div>
              )}
              {appointment.stripe_session_id && (
                <div className="space-y-1 md:col-span-2">
                  <p className="text-sm text-muted-foreground">Stripe Session ID</p>
                  <p className="font-mono text-xs">{appointment.stripe_session_id}</p>
                </div>
              )}
              {appointment.payment_notes && (
                <div className="space-y-1 md:col-span-2">
                  <p className="text-sm text-muted-foreground">Payment Notes</p>
                  <p className="text-sm bg-muted p-2 rounded">{appointment.payment_notes}</p>
                </div>
              )}
            </div>
            
            {/* Register Manual Payment Button */}
            {canEdit && (!appointment.payment_status || appointment.payment_status === 'pending') && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowManualPaymentDialog(true)}
                  className="gap-2"
                >
                  <Banknote className="h-4 w-4" />
                  Register Manual Payment
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Record an offline payment (cash, card terminal, check, etc.)
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Referral Information */}
          {appointment.referred_by && (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-3">Referral Source</h3>
                <p className="font-medium">
                  {appointment.referred_by}
                  {appointment.referred_other && `: ${appointment.referred_other}`}
                </p>
              </div>
              <Separator />
            </>
          )}

          {/* Metadata */}
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Created: {format(new Date(appointment.created_at), 'PPpp')}</p>
            <p>Last Updated: {format(new Date(appointment.updated_at), 'PPpp')}</p>
          </div>

          {/* Admin Actions */}
          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendEmails}
                disabled={loading === 'email' || isCancelled}
              >
                {loading === 'email' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Resend Emails
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRecreateCalendarEvent}
                disabled={loading === 'calendar' || !appointment.appointment_start || isCancelled}
              >
                {loading === 'calendar' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {appointment.calendar_event_id ? 'Recreate' : 'Create'} Calendar Event
              </Button>
              
              {/* Cancel Appointment Button */}
              {canCancel && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCancelDialog(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Appointment
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Manual Payment Dialog */}
      <ManualPaymentDialog
        appointment={appointment}
        open={showManualPaymentDialog}
        onOpenChange={setShowManualPaymentDialog}
        onPaymentCreated={onAppointmentUpdated}
      />

      {/* Cancel Appointment Dialog */}
      <CancelAppointmentDialog
        appointment={appointment}
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onAppointmentCancelled={onAppointmentUpdated}
      />
    </Dialog>
  );
};

export default AppointmentDetailDialog;
